let _profUserId = 'default'; try { require('@react-native-async-storage/async-storage').default.getItem('userToken').then(function(t){if(t)_profUserId=t.slice(0,16);}).catch(function(){}); } catch(e) {}
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  FlatList,
  Switch,
  Modal,
  Alert,
  RefreshControl,
  Animated,
  Platform,
  Dimensions,
} from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import NetInfo from '@react-native-community/netinfo';
import { apiFetch } from '../../utils/apiClient';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
// FIXED: Enhanced service import with comprehensive validation
let UnifiedIntegrationService = null;
try {
  const serviceModule = require('../../services/UnifiedIntegrationService');
  UnifiedIntegrationService = serviceModule.default || serviceModule;
  
  // Validate service has required methods
  if (!UnifiedIntegrationService || 
      typeof UnifiedIntegrationService.initialize !== 'function' ||
      typeof UnifiedIntegrationService.getNotifications !== 'function' ||
      typeof UnifiedIntegrationService.syncNotifications !== 'function') {
    console.warn('⚠️ UnifiedIntegrationService invalid, using mock implementation');
    UnifiedIntegrationService = null;
  } else {
    console.log('✅ UnifiedIntegrationService loaded successfully');
  }
} catch (error) {
  console.warn('⚠️ UnifiedIntegrationService not found, using mock implementation:', error.message);
  UnifiedIntegrationService = null;
}

// FIXED: Complete mock service implementation
const MockUnifiedIntegrationService = {
  initialize: async () => {
    console.log('📱 Mock UnifiedIntegrationService initialized');
    return true;
  },
  getNotifications: async () => {
    console.log('📬 Mock getNotifications called');
    return []; // Return empty array, will be filled with mock data
  },
  markNotificationAsRead: async (notificationId) => {
    console.log('✅ Mock markNotificationAsRead called:', notificationId);
    return true;
  },
  markAllNotificationsAsRead: async () => {
    console.log('✅ Mock markAllNotificationsAsRead called');
    return true;
  },
  updateNotificationSettings: async (settings) => {
    console.log('⚙️ Mock updateNotificationSettings called:', settings);
    return true;
  },
  getNotificationSettings: async () => {
    console.log('⚙️ Mock getNotificationSettings called');
    return {};
  },
  createNotification: async (notification) => {
    console.log('📝 Mock createNotification called:', notification);
    return { id: `mock_${Date.now()}`, ...notification };
  },
  deleteNotification: async (notificationId) => {
    console.log('🗑️ Mock deleteNotification called:', notificationId);
    return true;
  },
  syncNotifications: async () => {
    console.log('🔄 Mock syncNotifications called');
    return true;
  },
};

// Use the appropriate service
const IntegrationService = UnifiedIntegrationService || MockUnifiedIntegrationService;

const { width } = Dimensions.get('window');

export default function NotificationsScreen() {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  // === Live API Data ===
  const [userId, setUserId] = React.useState('default');
  React.useEffect(() => {
    AsyncStorage.getItem('userToken').then(t => { if (t) setUserId(t.slice(0, 16)); }).catch(() => {});
  }, []);
  React.useEffect(() => {
    if (userId === 'default') return;
    const _ck = `professional_${_profUserId}_notificationsscreen`;
    apiFetch('/api/data/notifications?sort=created_at&order=desc&limit=50').then(d => {
      if (d && !d.error) {
        const arr = Array.isArray(d) ? d : (d.data || d.results || []);
        AsyncStorage.setItem(_ck, JSON.stringify(arr)).catch(() => {});
      }
    }).catch(() => {});
  }, [userId]);

  const navigation = useNavigation();
  
  // Core States
  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [isOnline, setIsOnline] = useState(true);
  const [fadeAnim] = useState(new Animated.Value(0));
  const [serviceStatus, setServiceStatus] = useState('checking');
  
  // Notification States
  const [notifications, setNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [showSettings, setShowSettings] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Settings State
  const [notificationSettings, setNotificationSettings] = useState({
    applications: true,
    interviews: true,
    messages: true,
    jobAlerts: true,
    offers: true,
    assessments: true,
    networkUpdates: false,
    systemUpdates: true,
    pushNotifications: true,
    emailNotifications: true,
    smsNotifications: false,
  });

  // Filter options
  const filterOptions = [
    { key: 'all', label: 'All', icon: 'notifications', count: 0 },
    { key: 'unread', label: 'Unread', icon: 'radio-button-off', count: 0 },
    { key: 'application', label: 'Applications', icon: 'document-text', count: 0 },
    { key: 'interview', label: 'Interviews', icon: 'people', count: 0 },
    { key: 'message', label: 'Messages', icon: 'chatbubble', count: 0 },
    { key: 'offer', label: 'Offers', icon: 'checkmark-circle', count: 0 },
    { key: 'assessment', label: 'Assessments', icon: 'clipboard', count: 0 },
    { key: 'jobAlert', label: 'Job Alerts', icon: 'briefcase', count: 0 },
  ];

  // Initialize component
  useEffect(() => {
    initializeNotifications();
    setupNetworkMonitoring();
    startAnimations();
  }, []);

  // Focus effect for real-time updates
  useFocusEffect(
    React.useCallback(() => {
      loadNotifications();
      return () => {
        // Cleanup if needed
      };
    }, [])
  );

  // FIXED: Safe service initialization
  const initializeNotifications = async () => {
    try {
      setIsLoading(true);
      
      // Safe service initialization
      if (IntegrationService && typeof IntegrationService.initialize === 'function') {
        try {
          await IntegrationService.initialize();
          setServiceStatus(UnifiedIntegrationService ? 'connected' : 'mock');
          console.log('✅ Notification service initialized successfully');
        } catch (initError) {
          console.warn('⚠️ Service initialization failed:', initError.message);
          setServiceStatus('mock');
        }
      } else {
        setServiceStatus('mock');
        console.log('✅ Using mock notification service');
      }
      
      await loadNotifications();
      await loadNotificationSettings();
    } catch (error) {
      console.error('❌ Notifications initialization error:', error);
      setServiceStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  const setupNetworkMonitoring = () => {
    const unsubscribe = NetInfo.addEventListener(state => {
      setIsOnline(state.isConnected);
      if (state.isConnected) {
        // Sync notifications when coming back online
        syncNotifications();
      }
    });
    return unsubscribe;
  };

  const startAnimations = () => {
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 600,
      useNativeDriver: true,
    }).start();
  };

  // Real API-only notification loading. The previous version interleaved
  // hardcoded mock notifications with real data, which caused tap-handlers to
  // navigate with fake IDs (offer_1, app_1, etc.) and downstream screens to
  // fail with "not found in any data source" errors. Cleanup: real data only.
  const loadNotifications = async () => {
    try {
      let serviceNotifications = [];

      // Try the real API via apiFetch first (this is the trustworthy source)
      try {
        const apiRes = await apiFetch('/api/data/notifications?sort=created_at&order=desc&limit=50');
        if (apiRes && !apiRes.error) {
          const arr = Array.isArray(apiRes) ? apiRes : (apiRes.data || apiRes.results || []);
          // Map server columns → the shape the rest of this screen expects.
          // Adjust column names below if your `notifications` table differs.
          serviceNotifications = arr.map(r => ({
            id:           String(r.id),
            type:         r.type || 'message',
            title:        r.title || '',
            message:      r.message || r.body || '',
            timestamp:    r.timestamp || r.created_at || new Date().toISOString(),
            isRead:       !!(r.is_read || r.read || r.read_at),
            actionable:   !!r.actionable || !!(r.action_route || r.route),
            actionText:   r.action_text || r.actionText || 'View',
            actionRoute:  r.action_route || r.route || null,
            actionParams: r.action_params || r.params || {},
            priority:     r.priority || 'medium',
            category:     r.category || r.type || 'general',
          }));
          console.log('📬 Loaded notifications from API:', serviceNotifications.length);
        }
      } catch (apiError) {
        console.warn('⚠️ API notifications fetch failed (will fall back to cache):', apiError.message);
      }

      // Local cache as a secondary source (covers offline / API hiccups).
      let localNotifications = [];
      try {
        const storedNotifications = await AsyncStorage.getItem(`professional_${_profUserId}_notifications`);
        const parsed = storedNotifications ? JSON.parse(storedNotifications) : [];
        // Filter out stale mock entries that have invalid IDs
        localNotifications = parsed.filter(n => !n.id?.startsWith('mock_'));
      } catch (storageError) {
        console.warn('⚠️ Failed to load notifications from storage:', storageError.message);
      }

      // If API gave us data, prefer it. Otherwise show the cache so the user
      // sees *something* until the network recovers.
      const source = serviceNotifications.length > 0 ? serviceNotifications : localNotifications;

      // De-dupe + sort newest first
      const uniqueNotifications = source
        .filter((notification, index, self) =>
          index === self.findIndex(n => n.id === notification.id)
        )
        .sort((a, b) => new Date(b.timestamp || b.date) - new Date(a.timestamp || a.date));

      setNotifications(uniqueNotifications);
      filterNotifications(uniqueNotifications, selectedFilter);
      updateFilterCounts(uniqueNotifications);

      // Refresh the cache only if we have fresh API data
      if (serviceNotifications.length > 0) {
        try {
          await AsyncStorage.setItem(
            `professional_${_profUserId}_notifications`,
            JSON.stringify(uniqueNotifications)
          );
        } catch (saveError) {
          console.warn('⚠️ Failed to save notifications to storage:', saveError.message);
        }
      }
    } catch (error) {
      console.error('❌ Error loading notifications:', error);
      // No mock fallback. Empty state is honest.
      setNotifications([]);
      filterNotifications([], selectedFilter);
      updateFilterCounts([]);
    }
  };

  // FIXED: Safe settings loading
  const loadNotificationSettings = async () => {
    try {
      let serviceSettings = {};
      
      // Safe service call
      if (IntegrationService && typeof IntegrationService.getNotificationSettings === 'function') {
        try {
          serviceSettings = await IntegrationService.getNotificationSettings();
        } catch (serviceError) {
          console.warn('⚠️ Failed to get settings from service:', serviceError.message);
          serviceSettings = {};
        }
      }
      
      // Load from AsyncStorage
      let localSettings = {};
      try {
        const storedSettings = await AsyncStorage.getItem(`professional_${_profUserId}_notificationSettings`);
        localSettings = storedSettings ? JSON.parse(storedSettings) : {};
      } catch (storageError) {
        console.warn('⚠️ Failed to load settings from storage:', storageError.message);
        localSettings = {};
      }
      
      setNotificationSettings({
        ...notificationSettings,
        ...localSettings,
        ...serviceSettings,
      });
    } catch (error) {
      console.error('❌ Error loading notification settings:', error);
    }
  };

  // FIXED: Safe sync with error handling
  const syncNotifications = async () => {
    if (!isOnline) {
      console.log('📴 Offline - skipping sync');
      return;
    }
    
    try {
      if (IntegrationService && typeof IntegrationService.syncNotifications === 'function') {
        try {
          await IntegrationService.syncNotifications();
          await loadNotifications();
          console.log('🔄 Notifications synced successfully');
        } catch (syncError) {
          console.warn('⚠️ Sync failed:', syncError.message);
        }
      }
    } catch (error) {
      console.error('❌ Sync error:', error);
    }
  };

  // generateMockNotifications removed: notifications now come from real API only.

  const filterNotifications = (notificationsList, filter) => {
    let filtered = [];
    
    switch (filter) {
      case 'unread':
        filtered = notificationsList.filter(n => !n.isRead);
        break;
      case 'all':
        filtered = notificationsList;
        break;
      default:
        filtered = notificationsList.filter(n => n.type === filter || n.category === filter);
        break;
    }
    
    setFilteredNotifications(filtered);
  };

  const updateFilterCounts = (notificationsList) => {
    filterOptions.forEach(option => {
      switch (option.key) {
        case 'all':
          option.count = notificationsList.length;
          break;
        case 'unread':
          option.count = notificationsList.filter(n => !n.isRead).length;
          break;
        default:
          option.count = notificationsList.filter(n => n.type === option.key || n.category === option.key).length;
          break;
      }
    });
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadNotifications();
    await syncNotifications();
    setRefreshing(false);
  };

  // FIXED: Safe mark as read with error handling
  const markAsRead = async (notificationId) => {
    try {
      // Update local state
      const updatedNotifications = notifications.map(notification =>
        notification.id === notificationId
          ? { ...notification, isRead: true }
          : notification
      );
      
      setNotifications(updatedNotifications);
      filterNotifications(updatedNotifications, selectedFilter);
      updateFilterCounts(updatedNotifications);
      
      // Safe service call
      if (IntegrationService && typeof IntegrationService.markNotificationAsRead === 'function') {
        try {
          await IntegrationService.markNotificationAsRead(notificationId);
        } catch (serviceError) {
          console.warn('⚠️ Failed to mark as read in service:', serviceError.message);
        }
      }
      
      // Save to AsyncStorage
      try {
        await AsyncStorage.setItem(`professional_${_profUserId}_notifications`, JSON.stringify(updatedNotifications));
      } catch (storageError) {
        console.warn('⚠️ Failed to save to storage:', storageError.message);
      }
    } catch (error) {
      console.error('❌ Error marking notification as read:', error);
    }
  };

  // FIXED: Safe mark all as read
  const markAllAsRead = async () => {
    try {
      const updatedNotifications = notifications.map(notification => ({
        ...notification,
        isRead: true
      }));
      
      setNotifications(updatedNotifications);
      filterNotifications(updatedNotifications, selectedFilter);
      updateFilterCounts(updatedNotifications);
      
      // Safe service call
      if (IntegrationService && typeof IntegrationService.markAllNotificationsAsRead === 'function') {
        try {
          await IntegrationService.markAllNotificationsAsRead();
        } catch (serviceError) {
          console.warn('⚠️ Failed to mark all as read in service:', serviceError.message);
        }
      }
      
      // Save to AsyncStorage
      try {
        await AsyncStorage.setItem(`professional_${_profUserId}_notifications`, JSON.stringify(updatedNotifications));
      } catch (storageError) {
        console.warn('⚠️ Failed to save to storage:', storageError.message);
      }
    } catch (error) {
      console.error('❌ Error marking all notifications as read:', error);
    }
  };

  // FIXED: Safe delete notification
  const deleteNotification = async (notificationId) => {
    Alert.alert(
      'Delete Notification',
      'Are you sure you want to delete this notification?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              const updatedNotifications = notifications.filter(n => n.id !== notificationId);
              setNotifications(updatedNotifications);
              filterNotifications(updatedNotifications, selectedFilter);
              updateFilterCounts(updatedNotifications);
              
              // Safe service call
              if (IntegrationService && typeof IntegrationService.deleteNotification === 'function') {
                try {
                  await IntegrationService.deleteNotification(notificationId);
                } catch (serviceError) {
                  console.warn('⚠️ Failed to delete from service:', serviceError.message);
                }
              }
              
              // Save to AsyncStorage
              try {
                await AsyncStorage.setItem(`professional_${_profUserId}_notifications`, JSON.stringify(updatedNotifications));
              } catch (storageError) {
                console.warn('⚠️ Failed to save to storage:', storageError.message);
              }
            } catch (error) {
              console.error('❌ Error deleting notification:', error);
            }
          }
        }
      ]
    );
  };

  // FIXED: Safe settings toggle
  const toggleNotificationSetting = async (setting) => {
    try {
      const updatedSettings = {
        ...notificationSettings,
        [setting]: !notificationSettings[setting],
      };
      
      setNotificationSettings(updatedSettings);
      
      // Safe service call
      if (IntegrationService && typeof IntegrationService.updateNotificationSettings === 'function') {
        try {
          await IntegrationService.updateNotificationSettings(updatedSettings);
        } catch (serviceError) {
          console.warn('⚠️ Failed to update settings in service:', serviceError.message);
        }
      }
      
      // Save to AsyncStorage
      try {
        await AsyncStorage.setItem(`professional_${_profUserId}_notificationSettings`, JSON.stringify(updatedSettings));
      } catch (storageError) {
        console.warn('⚠️ Failed to save settings to storage:', storageError.message);
      }
    } catch (error) {
      console.error('❌ Error updating notification settings:', error);
    }
  };

  const handleNotificationPress = async (notification) => {
    // Mark as read
    if (!notification.isRead) {
      await markAsRead(notification.id);
    }
    
    // Navigate to appropriate screen
    if (notification.actionable && notification.actionRoute) {
      try {
        navigation.navigate(notification.actionRoute, notification.actionParams || {});
      } catch (navError) {
        console.warn('⚠️ Navigation failed:', navError.message);
        Alert.alert('Navigation Error', 'Unable to navigate to the requested screen.');
      }
    }
  };

  const getNotificationIcon = (type) => {
    const iconMap = {
      application: { name: 'document-text', color: '#0e4e96' },
      interview: { name: 'people', color: '#3b82f6' },
      message: { name: 'chatbubble', color: '#8b5cf6' },
      offer: { name: 'checkmark-circle', color: '#10b981' },
      jobAlert: { name: 'briefcase', color: '#f59e0b' },
      assessment: { name: 'clipboard', color: '#ec4899' },
      system: { name: 'information-circle', color: '#64748b' },
    };
    
    return iconMap[type] || { name: 'notifications', color: '#64748b' };
  };

  const getPriorityColor = (priority) => {
    const colorMap = {
      urgent: '#ef4444',
      high: '#f59e0b',
      medium: '#3b82f6',
      low: '#64748b',
    };
    
    return colorMap[priority] || '#64748b';
  };

  const formatTimestamp = (timestamp) => {
    try {
      const now = new Date();
      const notificationTime = new Date(timestamp);
      const diffInMinutes = Math.floor((now - notificationTime) / (1000 * 60));
      
      if (diffInMinutes < 1) return 'Just now';
      if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
      if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
      if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)}d ago`;
      
      return notificationTime.toLocaleDateString();
    } catch (error) {
      return 'Unknown time';
    }
  };

  const getUnreadCount = () => {
    return notifications.filter(notification => !notification.isRead).length;
  };

  const renderServiceStatus = () => {
    if (serviceStatus === 'checking') return null;
    
    const statusConfig = {
      connected: { color: '#10b981', text: 'Service Connected', icon: 'checkmark-circle' },
      mock: { color: '#f59e0b', text: 'Using Local Data', icon: 'warning' },
      error: { color: '#ef4444', text: 'Service Error', icon: 'alert-circle' },
    };
    
    const config = statusConfig[serviceStatus] || statusConfig.error;
    
    return (
      <View style={[styles.serviceStatus, { backgroundColor: config.color + '20' }]}>
        <Ionicons name={config.icon} size={16} color={config.color} />
        <Text style={[styles.serviceStatusText, { color: config.color }]}>
          {config.text}
        </Text>
      </View>
    );
  };

  const renderHeader = () => (
    <LinearGradient
      colors={['#0e4e96', '#1e5aa8']}
      style={styles.header}
    >
      <View style={styles.headerContent}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color="#FFFFFF" />
        </TouchableOpacity>

        <View style={styles.headerTitleContainer}>
          <Text style={styles.headerTitle}>Notifications</Text>
          {getUnreadCount() > 0 && (
            <View style={styles.unreadBadge}>
              <Text style={styles.unreadBadgeText}>{getUnreadCount()}</Text>
            </View>
          )}
        </View>

        <View style={styles.headerActions}>
          <TouchableOpacity
            style={styles.headerButton}
            onPress={() => setShowFilters(!showFilters)}
          >
            <Ionicons name="filter" size={20} color="#FFFFFF" />
          </TouchableOpacity>
          
          <TouchableOpacity
            style={styles.headerButton}
            onPress={() => setShowSettings(true)}
          >
            <Ionicons name="settings" size={20} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      </View>

      {renderServiceStatus()}
    </LinearGradient>
  );

  const renderFilterTabs = () => {
    if (!showFilters) return null;

    return (
      <View style={styles.filterContainer}>
        <ScrollView 
          horizontal 
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.filterScrollContent}
        >
          {filterOptions.map((filter) => (
            <TouchableOpacity
              key={filter.key}
              style={[
                styles.filterTab,
                selectedFilter === filter.key && styles.filterTabActive
              ]}
              onPress={() => {
                setSelectedFilter(filter.key);
                filterNotifications(notifications, filter.key);
              }}
            >
              <Ionicons 
                name={filter.icon} 
                size={16} 
                color={selectedFilter === filter.key ? '#FFFFFF' : '#64748b'} 
              />
              <Text style={[
                styles.filterTabText,
                selectedFilter === filter.key && styles.filterTabTextActive
              ]}>
                {filter.label}
              </Text>
              {filter.count > 0 && (
                <View style={[
                  styles.filterBadge,
                  selectedFilter === filter.key && styles.filterBadgeActive
                ]}>
                  <Text style={[
                    styles.filterBadgeText,
                    selectedFilter === filter.key && styles.filterBadgeTextActive
                  ]}>
                    {filter.count}
                  </Text>
                </View>
              )}
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>
    );
  };

  const renderNotificationItem = ({ item: notification }) => {
    const icon = getNotificationIcon(notification.type);
    const priorityColor = getPriorityColor(notification.priority);

    return (
      <TouchableOpacity
        style={[
          styles.notificationCard,
          !notification.isRead && styles.notificationCardUnread
        ]}
        onPress={() => handleNotificationPress(notification)}
        activeOpacity={0.7}
      >
        <View style={styles.notificationHeader}>
          <View style={styles.notificationIconContainer}>
            <View style={[styles.notificationIcon, { backgroundColor: icon.color + '20' }]}>
              <Ionicons name={icon.name} size={20} color={icon.color} />
            </View>
            {notification.priority === 'urgent' && (
              <View style={[styles.priorityIndicator, { backgroundColor: priorityColor }]} />
            )}
          </View>

          <View style={styles.notificationContent}>
            <View style={styles.notificationTitleRow}>
              <Text style={[
                styles.notificationTitle,
                !notification.isRead && styles.notificationTitleUnread
              ]}>
                {notification.title}
              </Text>
              <Text style={styles.notificationTime}>
                {formatTimestamp(notification.timestamp)}
              </Text>
            </View>

            <Text style={styles.notificationMessage} numberOfLines={2}>
              {notification.message}
            </Text>

            {notification.actionable && (
              <View style={styles.notificationActions}>
                <TouchableOpacity style={styles.actionButton}>
                  <Text style={styles.actionButtonText}>
                    {notification.actionText || 'View'}
                  </Text>
                  <Ionicons name="chevron-forward" size={16} color="#0e4e96" />
                </TouchableOpacity>
              </View>
            )}
          </View>

          <TouchableOpacity
            style={styles.deleteButton}
            onPress={() => deleteNotification(notification.id)}
          >
            <Ionicons name="close" size={20} color="#64748b" />
          </TouchableOpacity>
        </View>

        {!notification.isRead && <View style={styles.unreadIndicator} />}
      </TouchableOpacity>
    );
  };

  const renderSettingsModal = () => (
    <Modal
      visible={showSettings}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={() => setShowSettings(false)}
    >
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>Notification Settings</Text>
          <TouchableOpacity
            style={styles.modalCloseButton}
            onPress={() => setShowSettings(false)}
          >
            <Ionicons name="close" size={24} color="#64748b" />
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.settingsContent}>
          <View style={styles.settingsSection}>
            <Text style={styles.settingsSectionTitle}>Notification Types</Text>
            
            {[
              { key: 'applications', label: 'Job Applications', icon: 'document-text' },
              { key: 'interviews', label: 'Interviews', icon: 'people' },
              { key: 'messages', label: 'Messages', icon: 'chatbubble' },
              { key: 'jobAlerts', label: 'Job Alerts', icon: 'briefcase' },
              { key: 'offers', label: 'Job Offers', icon: 'checkmark-circle' },
              { key: 'assessments', label: 'Assessments', icon: 'clipboard' },
              { key: 'networkUpdates', label: 'Network Updates', icon: 'people-circle' },
              { key: 'systemUpdates', label: 'System Updates', icon: 'information-circle' },
            ].map((setting) => (
              <View key={setting.key} style={styles.settingItem}>
                <View style={styles.settingInfo}>
                  <Ionicons name={setting.icon} size={20} color="#64748b" />
                  <Text style={styles.settingLabel}>{setting.label}</Text>
                </View>
                <Switch
                  value={notificationSettings[setting.key]}
                  onValueChange={() => toggleNotificationSetting(setting.key)}
                  trackColor={{ false: '#e5e7eb', true: '#0e4e96' }}
                  thumbColor={notificationSettings[setting.key] ? '#FFFFFF' : '#f3f4f6'}
                />
              </View>
            ))}
          </View>

          <View style={styles.settingsSection}>
            <Text style={styles.settingsSectionTitle}>Delivery Methods</Text>
            
            {[
              { key: 'pushNotifications', label: 'Push Notifications', icon: 'notifications' },
              { key: 'emailNotifications', label: 'Email Notifications', icon: 'mail' },
              { key: 'smsNotifications', label: 'SMS Notifications', icon: 'chatbubble-ellipses' },
            ].map((setting) => (
              <View key={setting.key} style={styles.settingItem}>
                <View style={styles.settingInfo}>
                  <Ionicons name={setting.icon} size={20} color="#64748b" />
                  <Text style={styles.settingLabel}>{setting.label}</Text>
                </View>
                <Switch
                  value={notificationSettings[setting.key]}
                  onValueChange={() => toggleNotificationSetting(setting.key)}
                  trackColor={{ false: '#e5e7eb', true: '#0e4e96' }}
                  thumbColor={notificationSettings[setting.key] ? '#FFFFFF' : '#f3f4f6'}
                />
              </View>
            ))}
          </View>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      <Ionicons name="notifications-off" size={64} color="#d1d5db" />
      <Text style={styles.emptyStateTitle}>No Notifications</Text>
      <Text style={styles.emptyStateMessage}>
        {selectedFilter === 'all' 
          ? "You're all caught up! No notifications to show."
          : `No ${selectedFilter} notifications found.`
        }
      </Text>
      <TouchableOpacity 
        style={styles.refreshButton}
        onPress={onRefresh}
      >
        <Text style={styles.refreshButtonText}>Refresh</Text>
      </TouchableOpacity>
    </View>
  );

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
      
      <Animated.View style={[styles.content, { opacity: fadeAnim }]}>
        {renderHeader()}
        {renderFilterTabs()}

        {isLoading ? (
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingText}>Loading notifications...</Text>
          </View>
        ) : filteredNotifications.length === 0 ? (
          renderEmptyState()
        ) : (
          <FlatList
            data={filteredNotifications}
            renderItem={renderNotificationItem}
            keyExtractor={(item) => item.id}
            style={styles.notificationsList}
            contentContainerStyle={styles.notificationsListContent}
            showsVerticalScrollIndicator={false}
            refreshControl={
              <RefreshControl
                refreshing={refreshing}
                onRefresh={onRefresh}
                colors={['#0e4e96']}
                tintColor="#0e4e96"
              />
            }
          />
        )}

        {filteredNotifications.length > 0 && getUnreadCount() > 0 && (
          <TouchableOpacity
            style={styles.markAllReadButton}
            onPress={markAllAsRead}
          >
            <Text style={styles.markAllReadButtonText}>Mark All as Read</Text>
          </TouchableOpacity>
        )}
      </Animated.View>

      {renderSettingsModal()}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8fafc',
  },
  content: {
    flex: 1,
  },
  header: {
    paddingTop: Platform.OS === 'ios' ? 0 : StatusBar.currentHeight,
    paddingBottom: 16,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingTop: 16,
  },
  backButton: {
    padding: 8,
  },
  headerTitleContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: 16,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  unreadBadge: {
    backgroundColor: '#ef4444',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 2,
    marginLeft: 8,
    minWidth: 24,
    alignItems: 'center',
  },
  unreadBadgeText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '600',
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerButton: {
    padding: 8,
    marginLeft: 8,
  },
  serviceStatus: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginHorizontal: 16,
    marginTop: 8,
    borderRadius: 16,
  },
  serviceStatusText: {
    fontSize: 12,
    fontWeight: '500',
    marginLeft: 4,
  },
  filterContainer: {
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  filterScrollContent: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  filterTab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginRight: 12,
    borderRadius: 20,
    backgroundColor: '#f3f4f6',
  },
  filterTabActive: {
    backgroundColor: '#0e4e96',
  },
  filterTabText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#64748b',
    marginLeft: 6,
  },
  filterTabTextActive: {
    color: '#FFFFFF',
  },
  filterBadge: {
    backgroundColor: '#e5e7eb',
    borderRadius: 10,
    paddingHorizontal: 6,
    paddingVertical: 2,
    marginLeft: 6,
    minWidth: 20,
    alignItems: 'center',
  },
  filterBadgeActive: {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
  },
  filterBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#64748b',
  },
  filterBadgeTextActive: {
    color: '#FFFFFF',
  },
  notificationsList: {
    flex: 1,
  },
  notificationsListContent: {
    padding: 16,
  },
  notificationCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    borderLeftWidth: 4,
    borderLeftColor: 'transparent',
  },
  notificationCardUnread: {
    borderLeftColor: '#0e4e96',
    backgroundColor: '#f8faff',
  },
  notificationHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  notificationIconContainer: {
    position: 'relative',
    marginRight: 12,
  },
  notificationIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  priorityIndicator: {
    position: 'absolute',
    top: -2,
    right: -2,
    width: 12,
    height: 12,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: '#FFFFFF',
  },
  notificationContent: {
    flex: 1,
  },
  notificationTitleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 4,
  },
  notificationTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#1f2937',
    flex: 1,
    marginRight: 8,
  },
  notificationTitleUnread: {
    fontWeight: '600',
    color: '#0e4e96',
  },
  notificationTime: {
    fontSize: 12,
    color: '#64748b',
  },
  notificationMessage: {
    fontSize: 14,
    color: '#64748b',
    lineHeight: 20,
    marginBottom: 8,
  },
  notificationActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
  },
  actionButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#0e4e96',
    marginRight: 4,
  },
  deleteButton: {
    padding: 4,
    marginLeft: 8,
  },
  unreadIndicator: {
    position: 'absolute',
    top: 16,
    right: 16,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#0e4e96',
  },
  markAllReadButton: {
    backgroundColor: '#0e4e96',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    margin: 16,
    alignItems: 'center',
  },
  markAllReadButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
  },
  loadingText: {
    fontSize: 16,
    color: '#64748b',
    marginTop: 16,
  },
  emptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
  },
  emptyStateTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#1f2937',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyStateMessage: {
    fontSize: 16,
    color: '#64748b',
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 24,
  },
  refreshButton: {
    backgroundColor: '#0e4e96',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
  },
  refreshButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  modalContainer: {
    flex: 1,
    backgroundColor: '#f8fafc',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 16,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1f2937',
  },
  modalCloseButton: {
    padding: 4,
  },
  settingsContent: {
    flex: 1,
  },
  settingsSection: {
    backgroundColor: '#FFFFFF',
    marginTop: 16,
    paddingVertical: 8,
  },
  settingsSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1f2937',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#f8fafc',
  },
  settingItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  settingInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  settingLabel: {
    fontSize: 16,
    color: '#1f2937',
    marginLeft: 12,
  },
});
